import { MediaType } from '@qse/api'
import * as Sentry from '@sentry/react'
import { create } from 'zustand'

export type DownloadStatus = 'PROCESSING' | 'DECRYPTING' | 'PREPARING' | 'AVAILABLE' | 'FAILED'

export interface DownloadItemState {
  id: string
  fileId?: string
  name: string
  mediaType?: MediaType
  progress?: number
  status: DownloadStatus
  errorMessage?: string
  url?: string
  createdAt: number
  mimeType?: string
  processingStartTime?: number // Track when processing started for minimum duration
}

interface GlobalFileDownloadState {
  isOpen: boolean
  isMinimized: boolean
  items: DownloadItemState[]
  downloadQueue: string[]
  isProcessingQueue: boolean
  setOpen: (open: boolean) => void
  minimize: () => void
  expand: () => void
  toggle: () => void
  clearCompleted: () => void
  appendOrUpdate: (item: Partial<DownloadItemState> & { id: string }) => void
  startRequest: (params: {
    id: string
    fileId?: string
    name: string
    mediaType?: MediaType
    mimeType?: string
  }) => void
  markProcessing: (id: string) => void
  markPreparing: (id: string, progress?: number) => void
  markDecrypting: (id: string, progress?: number) => void
  markAvailable: (id: string, url?: string) => void
  markFailed: (id: string, error?: string) => void
  retry: (id: string) => void
  hasActiveDownloads: () => boolean
  getActiveDownloadCount: () => number
  processDownloadQueue: () => void
  safeDownload: (name: string, url: string, signal?: AbortSignal) => Promise<void>
  reset: () => void
}

export const useGlobalFileDownload = create<GlobalFileDownloadState>((set, get) => ({
  isOpen: false,
  isMinimized: false,
  items: [],
  // work around for multiple downloads
  downloadQueue: [],
  isProcessingQueue: false,
  setOpen: open => set({ isOpen: open }),
  minimize: () => set({ isMinimized: true }),
  expand: () => set({ isMinimized: false }),
  toggle: () => set(state => ({ isMinimized: !state.isMinimized })),
  clearCompleted: () =>
    set(state => ({
      items: state.items.filter(
        i => i.status === 'PROCESSING' || i.status === 'DECRYPTING' || i.status === 'PREPARING',
      ),
    })),
  /**
   * Appends a new download item or updates an existing one.
   * @param item - Download item to add or update
   */
  appendOrUpdate: item => {
    set(state => {
      const existingIndex = state.items.findIndex(i => i.id === item.id)
      const updated: DownloadItemState = {
        id: item.id,
        fileId: item.fileId,
        name: item.name || (existingIndex >= 0 ? state.items[existingIndex].name : 'download'),
        mediaType: item.mediaType ?? state.items[existingIndex]?.mediaType,
        progress: item.progress ?? state.items[existingIndex]?.progress,
        status:
          (item.status as DownloadStatus) ?? state.items[existingIndex]?.status ?? 'PROCESSING',
        errorMessage: item.errorMessage ?? state.items[existingIndex]?.errorMessage,
        url: item.url ?? state.items[existingIndex]?.url,
        createdAt: state.items[existingIndex]?.createdAt ?? Date.now(),
        mimeType: item.mimeType ?? state.items[existingIndex]?.mimeType,
      }
      const newItems = [...state.items]

      if (existingIndex >= 0) newItems[existingIndex] = updated
      else newItems.unshift(updated)

      // cap to 10 items
      return { items: newItems.slice(0, 50), isOpen: true }
    })
  },
  /**
   * Starts a new download request.
   * @param params - Download request parameters
   */
  startRequest: ({ id, fileId, name, mediaType, mimeType }) => {
    if (get().items.find(i => i.id === id)) {
      // To check if the item is already in the list, we need to update the item with the new params and make at the top of the list
      set(state => ({
        isOpen: true,
        items: [
          {
            id,
            fileId,
            name,
            mediaType,
            status: 'PROCESSING' as DownloadStatus,
            createdAt: Date.now(),
            mimeType,
            processingStartTime: Date.now(),
          },
          ...state.items.filter(i => i.id !== id),
        ].slice(0, 50),
      }))

      return
    }

    set(state => ({
      isOpen: true,
      items: [
        {
          id,
          fileId,
          name,
          mediaType,
          status: 'PROCESSING' as DownloadStatus,
          createdAt: Date.now(),
          mimeType,
          processingStartTime: Date.now(),
        },
        ...state.items,
      ].slice(0, 50),
    }))
  },
  markProcessing: id =>
    set(state => ({
      items: state.items.map(i =>
        i.id === id
          ? { ...i, status: 'PROCESSING', errorMessage: undefined, processingStartTime: Date.now() }
          : i,
      ),
    })),
  markPreparing: (id, progress) =>
    set(state => ({
      items: state.items.map(i =>
        i.id === id ? { ...i, status: 'PREPARING', progress, processingStartTime: Date.now() } : i,
      ),
    })),
  markDecrypting: (id, progress) =>
    set(state => ({
      items: state.items.map(i =>
        i.id === id ? { ...i, status: 'DECRYPTING', progress, processingStartTime: Date.now() } : i,
      ),
    })),
  markAvailable: (id, url) => {
    const currentState = get()
    const item = currentState.items.find(i => i.id === id)

    if (
      item &&
      (item.status === 'DECRYPTING' || item.status === 'PROCESSING' || item.status === 'PREPARING')
    ) {
      // If changing from a processing status to AVAILABLE, add a minimum delay
      const timeSinceProcessingStart = Date.now() - (item.processingStartTime || Date.now())
      const minimumDuration = 3000 // 3 seconds

      if (timeSinceProcessingStart < minimumDuration) {
        const remainingTime = minimumDuration - timeSinceProcessingStart

        setTimeout(() => {
          set(state => ({
            items: state.items.map(i =>
              i.id === id ? { ...i, status: 'AVAILABLE', progress: 100, url } : i,
            ),
            downloadQueue: [...state.downloadQueue, id],
          }))

          // Process queue after adding to it
          setTimeout(() => get().processDownloadQueue(), 100)
        }, remainingTime)

        return
      }
    }

    // Immediate status update for other cases
    set(state => ({
      items: state.items.map(i =>
        i.id === id ? { ...i, status: 'AVAILABLE', progress: 100, url } : i,
      ),
      downloadQueue: [...state.downloadQueue, id],
    }))

    // Process queue after adding to it
    setTimeout(() => get().processDownloadQueue(), 100)
  },
  markFailed: (id, error) => {
    set(state => ({
      items: state.items.map(i =>
        i.id === id ? { ...i, status: 'FAILED', errorMessage: error || i.errorMessage } : i,
      ),
    }))
  },
  retry: id => {
    set(state => ({
      items: state.items.map(i =>
        i.id === id ? { ...i, status: 'PROCESSING', progress: 0, errorMessage: undefined } : i,
      ),
    }))
  },

  // Safe download function that prevents navigation issues
  safeDownload: async (name: string, url: string, signal?: AbortSignal) => {
    // Sanitize file name for Sentry
    const sanitizeFileName = (fileName: string): string => {
      const parts = fileName.split(/[/\\]/)
      const name = parts[parts.length - 1] || fileName
      const lastDot = name.lastIndexOf('.')

      if (lastDot > 0) {
        return name.substring(0, lastDot) + '[ext]'
      }

      return name
    }

    const sanitizedName = sanitizeFileName(name)

    return Sentry.startSpan(
      {
        op: 'file.download',
        name: `Download ${sanitizedName}`,
      },
      async span => {
        span.setAttribute('file.name', sanitizedName)
        span.setAttribute('file.url', url.split('?')[0]) // Remove query params

        try {
          const res = await fetch(url, {
            signal,
          })

          if (!res.ok) throw new Error(`Download failed: ${res.status}`)

          const blob = await res.blob()

          span.setAttribute('file.size', blob.size)
          const objectUrl = URL.createObjectURL(blob)

          try {
            const a = document.createElement('a')

            a.href = objectUrl
            a.download = name || 'download'
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            span.setStatus({ code: 1 }) // 1 = OK
          } finally {
            // Always clean up the object URL to prevent memory leaks
            URL.revokeObjectURL(objectUrl)
          }
        } catch (error) {
          console.error('Download failed:', error)
          span.setStatus({ code: 2 }) // 2 = ERROR
          span.setAttribute('error', true)
          throw error
        }
      },
    )
  },

  processDownloadQueue: async () => {
    const currentState = get()

    if (currentState.isProcessingQueue || currentState.downloadQueue.length === 0) {
      return
    }

    set(state => ({ ...state, isProcessingQueue: true }))

    // Get a copy of the current queue to work with
    const queueToProcess = [...currentState.downloadQueue]

    // Clear the queue immediately
    set(state => ({ ...state, downloadQueue: [] }))

    for (const downloadId of queueToProcess) {
      const item = currentState.items.find(i => i.id === downloadId)

      if (item?.url) {
        try {
          // Use safe download instead of direct link creation
          await get().safeDownload(item.name, item.url)

          // Minimal delay since we're not navigating anymore
          if (queueToProcess.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 300))
          }
        } catch (error) {
          // Mark as failed if download fails
          get().markFailed(downloadId, error instanceof Error ? error.message : 'Download failed')
        }
      }
    }

    set(state => ({ ...state, isProcessingQueue: false }))
  },
  /**
   * Checks if there are any downloads currently in progress.
   * @returns true if there are active downloads, false otherwise
   */
  hasActiveDownloads: () => {
    const state = get()

    return state.items.some(
      i => i.status === 'PROCESSING' || i.status === 'DECRYPTING' || i.status === 'PREPARING',
    )
  },
  /**
   * Gets the count of active downloads.
   * @returns number of downloads currently in progress
   */
  getActiveDownloadCount: () => {
    const state = get()

    return state.items.filter(
      i => i.status === 'PROCESSING' || i.status === 'DECRYPTING' || i.status === 'PREPARING',
    ).length
  },

  // Reset all download state - useful for logout
  reset: () => {
    set({
      isOpen: false,
      isMinimized: false,
      items: [],
      downloadQueue: [],
      isProcessingQueue: false,
    })
  },
}))

// Global reset function that can be called from anywhere
export const resetGlobalFileDownload = () => {
  const store = useGlobalFileDownload.getState()

  store.reset()
}

export const getDownloadSummary = (items: DownloadItemState[]) => {
  const failed = items.filter(i => i.status === 'FAILED').length
  const complete = items.filter(i => i.status === 'AVAILABLE').length
  const inProgress = items.filter(
    i => i.status === 'PROCESSING' || i.status === 'DECRYPTING' || i.status === 'PREPARING',
  ).length

  if (inProgress > 0) return `${inProgress} downloads in progress`
  if (failed > 0 && complete > 0) return `${complete} downloads complete, ${failed} failed`
  if (failed > 0) return `0 downloads complete, ${failed} failed`
  if (complete > 0) return `${complete} downloads complete`

  return 'Downloads'
}
